import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Pencil, Plus, X, Copy, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { ROLES } from "@bo/shared";
import { PageHeader } from "@/components/ui/PageHeader.js";
import { DataTable, type Column } from "@/components/ui/DataTable.js";
import { StatusBadge } from "@/components/ui/StatusBadge.js";
import { CompanyCombobox } from "@/components/ui/CompanyCombobox.js";
import { api } from "@/lib/api.js";
import { relativeTime } from "@/lib/format.js";
import { useAuthStore } from "@/lib/auth.js";

interface BoUser {
  id: number;
  email: string;
  fullName: string;
  status: string;
  roles: string[];
  ginaIdcompany: number | null;
  lastLoginAt: string | null;
}

const ROLE_OPTIONS: { code: string; label: string; hint: string }[] = [
  { code: ROLES.SUPERADMIN, label: "Superadmin", hint: "Acceso total" },
  { code: ROLES.BOTS_OPS, label: "Bots Ops", hint: "Operación de bots / canales" },
  { code: ROLES.ADMIN_COMERCIAL, label: "Admin Comercial", hint: "Gestión comercial completa" },
  { code: ROLES.POSTVENTA, label: "Postventa", hint: "Consulta y soporte postventa" },
];

const STATUS_OPTIONS = ["ACTIVE", "SUSPENDED", "DISABLED"];

function cryptoRandom(): number {
  const arr = new Uint32Array(1);
  crypto.getRandomValues(arr);
  return (arr[0] ?? 0) / 2 ** 32;
}

function genPassword(len = 16): string {
  const upper = "ABCDEFGHJKMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnpqrstuvwxyz";
  const digits = "23456789";
  const symbols = "!@#$%&*+-";
  const all = upper + lower + digits + symbols;
  const pick = (set: string) => set.charAt(Math.floor(cryptoRandom() * set.length));
  // Garantiza al menos uno de cada clase
  const chars: string[] = [pick(upper), pick(lower), pick(digits), pick(symbols)];
  while (chars.length < len) chars.push(pick(all));
  // Shuffle Fisher-Yates con randomness criptográfico
  for (let i = chars.length - 1; i > 0; i--) {
    const j = Math.floor(cryptoRandom() * (i + 1));
    const tmp = chars[i] ?? "";
    chars[i] = chars[j] ?? "";
    chars[j] = tmp;
  }
  return chars.join("");
}

function UserModal({
  editing,
  onClose,
  onSaved,
}: {
  editing: BoUser | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!editing;
  const [email, setEmail] = useState(editing?.email ?? "");
  const [fullName, setFullName] = useState(editing?.fullName ?? "");
  const [password, setPassword] = useState("");
  const [roles, setRoles] = useState<string[]>(editing?.roles ?? []);
  const [status, setStatus] = useState(editing?.status ?? "ACTIVE");
  const [ginaCompany, setGinaCompany] = useState<number | null>(
    editing?.ginaIdcompany ?? null,
  );
  const [mustChange, setMustChange] = useState(true);

  const toggleRole = (code: string) =>
    setRoles((rs) => (rs.includes(code) ? rs.filter((r) => r !== code) : [...rs, code]));

  const save = useMutation({
    mutationFn: () => {
      if (isEdit) {
        return api.patch(`/users/${editing!.id}`, {
          fullName,
          status,
          roles,
          ginaIdcompany: ginaCompany,
        });
      }
      return api.post("/users", {
        email,
        fullName,
        password,
        roles,
        ginaIdcompany: ginaCompany ?? undefined,
        mustChangePassword: mustChange,
      });
    },
    onSuccess: () => {
      toast.success(
        isEdit
          ? "Usuario actualizado"
          : "Usuario creado. Comparte la contraseña temporal; deberá cambiarla al ingresar.",
      );
      onSaved();
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  const canSave =
    fullName.trim().length > 0 &&
    roles.length > 0 &&
    (isEdit || (emailValid && password.length >= 12)) &&
    !save.isPending;

  return (
    <div className="fixed inset-0 bg-slate-950/40 z-50 grid place-items-center p-4">
      <div className="card w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-auto">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-semibold text-lg">
              {isEdit ? "Editar usuario" : "Nuevo usuario del backoffice"}
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Cuentas que acceden a este panel (no son agentes de Gina).
            </p>
          </div>
          <button className="btn-ghost p-1" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Email *</label>
          <input
            className="input"
            type="email"
            value={email}
            disabled={isEdit}
            placeholder="persona@isoft-ste.com"
            onChange={(e) => setEmail(e.target.value)}
          />
          {!isEdit && email.length > 0 && !emailValid && (
            <p className="text-xs text-rose-600">Email no válido.</p>
          )}
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Nombre completo *</label>
          <input
            className="input"
            value={fullName}
            placeholder="Nombre y apellido"
            onChange={(e) => setFullName(e.target.value)}
          />
        </div>

        {!isEdit && (
          <div className="space-y-1">
            <label className="text-sm font-medium">Contraseña temporal *</label>
            <div className="flex gap-2">
              <input
                className="input mono"
                value={password}
                placeholder="Mínimo 12 caracteres"
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="btn-secondary shrink-0"
                title="Generar contraseña segura"
                onClick={() => setPassword(genPassword())}
              >
                <RefreshCw size={14} className="mr-1" /> Generar
              </button>
              {password && (
                <button
                  type="button"
                  className="btn-ghost shrink-0 p-2"
                  title="Copiar"
                  onClick={() => {
                    navigator.clipboard.writeText(password);
                    toast.success("Contraseña copiada");
                  }}
                >
                  <Copy size={14} />
                </button>
              )}
            </div>
            {password.length > 0 && password.length < 12 && (
              <p className="text-xs text-rose-600">Mínimo 12 caracteres.</p>
            )}
          </div>
        )}

        <div className="space-y-1">
          <label className="text-sm font-medium">Roles *</label>
          <div className="grid grid-cols-2 gap-2">
            {ROLE_OPTIONS.map((r) => (
              <label
                key={r.code}
                className={`flex items-start gap-2 rounded-md border p-2 cursor-pointer text-sm ${
                  roles.includes(r.code)
                    ? "border-blue-400 bg-blue-50/60 dark:bg-blue-950/30"
                    : "border-slate-200 dark:border-slate-700"
                }`}
              >
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={roles.includes(r.code)}
                  onChange={() => toggleRole(r.code)}
                />
                <span>
                  <span className="font-medium block">{r.label}</span>
                  <span className="text-xs text-slate-500">{r.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        {isEdit && (
          <div className="space-y-1">
            <label className="text-sm font-medium">Estado</label>
            <select
              className="input"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="space-y-1">
          <label className="text-sm font-medium">
            Company de Gina (opcional)
          </label>
          <CompanyCombobox
            value={ginaCompany}
            onChange={(id) => setGinaCompany(id)}
            placeholder="Acota el alcance a una company (opcional)…"
          />
        </div>

        {!isEdit && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={mustChange}
              onChange={(e) => setMustChange(e.target.checked)}
            />
            Obligar cambio de contraseña en el primer login
          </label>
        )}

        <div className="flex gap-2 pt-2">
          <button className="btn-secondary flex-1" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="btn-primary flex-1"
            disabled={!canSave}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Guardando…" : isEdit ? "Guardar" : "Crear usuario"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordModal({
  user,
  onClose,
}: {
  user: BoUser;
  onClose: () => void;
}) {
  const [pw, setPw] = useState("");
  const reset = useMutation({
    mutationFn: () =>
      api.post(`/users/${user.id}/reset-password`, { newPassword: pw }),
    onSuccess: () => {
      toast.success(
        "Contraseña restablecida. Se cerraron las sesiones del usuario.",
      );
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="fixed inset-0 bg-slate-950/40 z-50 grid place-items-center p-4">
      <div className="card w-full max-w-md p-6 space-y-4">
        <div className="flex items-start justify-between">
          <h2 className="font-semibold text-lg">Restablecer contraseña</h2>
          <button className="btn-ghost p-1" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <p className="text-sm text-slate-500">
          Para <span className="font-medium">{user.email}</span>. Esto invalida
          sus sesiones activas en el backoffice.
        </p>
        <div className="flex gap-2">
          <input
            className="input mono"
            value={pw}
            placeholder="Nueva contraseña (mín. 12)"
            onChange={(e) => setPw(e.target.value)}
          />
          <button
            type="button"
            className="btn-secondary shrink-0"
            onClick={() => setPw(genPassword())}
          >
            <RefreshCw size={14} className="mr-1" /> Generar
          </button>
          {pw && (
            <button
              type="button"
              className="btn-ghost shrink-0 p-2"
              title="Copiar"
              onClick={() => {
                navigator.clipboard.writeText(pw);
                toast.success("Copiada");
              }}
            >
              <Copy size={14} />
            </button>
          )}
        </div>
        {pw.length > 0 && pw.length < 12 && (
          <p className="text-xs text-rose-600">Mínimo 12 caracteres.</p>
        )}
        <div className="flex gap-2">
          <button className="btn-secondary flex-1" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="btn-primary flex-1"
            disabled={pw.length < 12 || reset.isPending}
            onClick={() => reset.mutate()}
          >
            {reset.isPending ? "Aplicando…" : "Restablecer"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function UsersPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<BoUser | null>(null);
  const [resetting, setResetting] = useState<BoUser | null>(null);

  const canWrite = useAuthStore((s) => s.hasPermission("user.write"));
  const canReset = useAuthStore((s) => s.hasPermission("user.reset_password"));

  const { data, isLoading } = useQuery({
    queryKey: ["users", q],
    queryFn: () =>
      api.get<{ items: BoUser[] }>(
        `/users?pageSize=100${q ? `&q=${encodeURIComponent(q)}` : ""}`,
      ),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["users"] });

  const cols: Column<BoUser>[] = useMemo(() => {
    const base: Column<BoUser>[] = [
      { header: "Email", render: (r) => <span className="mono text-xs">{r.email}</span> },
      { header: "Nombre", render: (r) => <span className="font-medium">{r.fullName}</span> },
      {
        header: "Roles",
        render: (r) => (
          <div className="flex gap-1 flex-wrap">
            {r.roles.map((rol) => (
              <span key={rol} className="badge-info">{rol}</span>
            ))}
          </div>
        ),
      },
      { header: "Company", render: (r) => r.ginaIdcompany ?? "—" },
      { header: "Estado", render: (r) => <StatusBadge value={r.status} /> },
      { header: "Último login", render: (r) => relativeTime(r.lastLoginAt) },
    ];
    if (canWrite || canReset) {
      base.push({
        header: "",
        width: "150px",
        render: (r) => (
          <div className="flex gap-1 justify-end">
            {canWrite && (
              <button
                className="btn-ghost p-1.5"
                title="Editar"
                onClick={() => setEditing(r)}
              >
                <Pencil size={14} />
              </button>
            )}
            {canReset && (
              <button
                className="btn-ghost p-1.5"
                title="Restablecer contraseña"
                onClick={() => setResetting(r)}
              >
                <KeyRound size={14} />
              </button>
            )}
          </div>
        ),
      });
    }
    return base;
  }, [canWrite, canReset]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Usuarios del backoffice"
        description="Administración de cuentas que acceden a este panel."
        actions={
          canWrite ? (
            <button className="btn-primary" onClick={() => setCreating(true)}>
              <Plus size={14} className="mr-1" /> Nuevo usuario
            </button>
          ) : undefined
        }
      />

      <div className="flex gap-3">
        <input
          className="input max-w-sm"
          placeholder="Buscar por email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <DataTable
        columns={cols}
        rows={data?.items}
        loading={isLoading}
        getKey={(r) => r.id}
        empty="No hay usuarios registrados."
      />

      {(creating || editing) && (
        <UserModal
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={refresh}
        />
      )}
      {resetting && (
        <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} />
      )}
    </div>
  );
}
